import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { AppHeader } from '../components/layout/AppHeader';
import { ProjectCard } from '../components/dashboard/ProjectCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Search, Plus, Video, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'scripting', label: 'Scripting' },
  { key: 'rendering', label: 'Rendering' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
];

export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search.trim()) params.search = search.trim();
      const res = await projectsAPI.list(params);
      setProjects(res.data.projects);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    fetchProjects();
    // Poll for status updates every 10 seconds
    const interval = setInterval(fetchProjects, 10000);
    return () => clearInterval(interval);
  }, [fetchProjects]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await projectsAPI.delete(id);
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const handleRetry = async (id) => {
    try {
      await projectsAPI.retryVideo(id);
      toast.success('Retrying...');
      fetchProjects();
    } catch (err) {
      toast.error('Retry failed');
    }
  };

  return (
    <div className="min-h-screen bg-background app-noise">
      <AppHeader />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative z-10">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Projects</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your avatar video projects</p>
          </div>
          <Button
            onClick={() => navigate('/projects/new')}
            className="rounded-xl gap-1.5 sm:hidden"
            data-testid="new-project-button-mobile"
          >
            <Plus className="w-4 h-4" /> New project
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-xl"
              data-testid="dashboard-project-search-input"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="dashboard-status-filter">
            {FILTERS.map((f) => (
              <Button
                key={f.key}
                variant={filter === f.key ? 'default' : 'secondary'}
                size="sm"
                className="rounded-xl text-xs h-8"
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Project grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-56 rounded-xl" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <FolderOpen className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-lg font-semibold mb-1">
              {search || filter !== 'all' ? 'No matching projects' : 'No projects yet'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              {search || filter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first avatar video project to get started'}
            </p>
            {!search && filter === 'all' && (
              <Button onClick={() => navigate('/projects/new')} className="rounded-xl gap-1.5" data-testid="new-project-button-empty">
                <Video className="w-4 h-4" /> Create first project
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project._id}
                project={project}
                onDelete={handleDelete}
                onRetry={handleRetry}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
